/* eslint-disable react/jsx-key */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import './dashboard.css';
import { DrugExceptionsClient } from '../../../client/drug-exceptions-client';
import { DrugExceptionResponse, DrugExceptionStatus } from '../../../model/Reformulary';
import { format } from 'date-fns';
import { TableHead, TableContainer, Table, TableRow, TableCell, TableSortLabel, TableBody, TablePagination, Paper } from '@mui/material';
import { useTable, useSortBy } from 'react-table';
import {useNavigate} from "react-router-dom";

interface HandleError {
    handleError: Function;
}

interface TableData {
    submissionDate: string;
    caseId: number;
    memberName: string;
    assessorName?: string;
    status: string;
}

export default function AssessorDashboard(props: HandleError) {

    const history = useNavigate();
    const userId = JSON.parse(sessionStorage.getItem('userId') || '');
    const drugExceptionsClient = new DrugExceptionsClient();
    const monthList = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const [exceptionList, setExceptionList] = useState<DrugExceptionResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);

    function getStatusText(ref: string) {
        let displayString = '';
        switch (ref) {
            case (DrugExceptionStatus[DrugExceptionStatus.UNDER_REVIEW]):
                displayString = 'Under Review';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.APPROVED]):
                displayString = 'Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.INCOMPLETE]):
                displayString = 'Incomplete';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.NOT_APPROVED]):
                displayString = 'Not Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.EXPIRED]):
                displayString = 'Not Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.ASSESSED_APPROVED]):
                displayString = 'Assessed/Approved';
                break;
            case (DrugExceptionStatus[DrugExceptionStatus.ASSESSED_NOT_APPROVED]):
                displayString = 'Assessed/Not Approved';
                break;
            default:
                displayString = '';
                break;
        }
        return displayString;
    }

    async function fetchData() {
        setLoading(true);
        let drugExceptionResponse;
        if (userId) {
            const token = userId.authToken;
            const id = userId.id;
            if (token && id) {
                drugExceptionResponse = await drugExceptionsClient.api.get('/drugexceptions', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }
            setExceptionList(drugExceptionResponse?.data.reverse());
        }
        setLoading(false);
    }

    const columns = React.useMemo(
        () => [
            {
                Header: 'Submission Date',
                accessor: 'submissionDate'
            },
            {
                Header: 'Case ID',
                accessor: 'caseId'
            },
            {
                Header: 'Member Name',
                accessor: 'memberName'
            },
            {
                Header: 'Claimant Name',
                accessor: 'claimantName'
            },
            {
                Header: 'Status',
                accessor: 'status'
            }
        ],
        []
    );

    const data = useMemo<TableData[]>(() =>
        exceptionList.map<TableData>((e) => ({
            submissionDate: format(new Date(e.submittedDate), 'MMM dd yyyy'),
            caseId: e.id,
            memberName: `${e.claimant?.memberFirstName} ${e.claimant?.memberLastName}`,
            claimantName: `${e.claimant?.firstName} ${e.claimant?.lastName}`,
            status: getStatusText(e.drugExceptionStatus)
        })), [exceptionList]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = useTable<TableData>(
        { columns, data },
        useSortBy
    );

    useEffect(() => {
        fetchData();
    }, []);

    const openExceptionDetails = (event: React.MouseEvent<unknown>, drugExceptionId: number) => {
        const drugException = exceptionList.filter(e => e.id === drugExceptionId);
        sessionStorage.setItem('selectedDrugException', JSON.stringify(drugException[0]));
        history('/assessor/approve');
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <div className="assessor-dashboard-container">
            <h1 className='primary-header'>DASHBOARD</h1>
            <Paper>
            <TableContainer>
                <Table {...getTableProps()}>
                    <TableHead>
                        {headerGroups.map(headerGroup => (
                            <TableRow {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <TableCell
                                        {...(column.id === 'selection'
                                            ? column.getHeaderProps()
                                            : column.getHeaderProps(column.getSortByToggleProps()))}
                                    >
                                        {column.render('Header')}
                                        {column.id !== 'selection' ? (
                                            <TableSortLabel
                                                active={column.isSorted}
                                                direction={column.isSortedDesc ? 'desc' : 'asc'}
                                            />
                                        ) : null}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5}>Loading...</TableCell>
                            </TableRow>
                        ) : (
                                rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => {
                                    prepareRow(row);
                                    return (
                                        <TableRow hover onClick={ (event) => openExceptionDetails(event, row.original.caseId) } {...row.getRowProps()}>
                                            {row.cells.map(cell => (
                                                <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })
                            )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100, 200]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            </Paper>
        </div>
    );
}